/**
 * Payment plan eligibility and installment calculation logic.
 *
 * These helpers run on both the Next.js frontend (cart UI) and the
 * API route that creates subscription checkout sessions.
 */

import type { PaymentPlanConfig, InstallmentBreakdown } from '@/models/paymentPlan';
import type { TicketType, PricingTier } from '@/models/ticketType';
import { calculateDiscountedPrice } from '@/lib/pricing';

/** Minimum cart total (in cents) to qualify for a payment plan */
export const PAYMENT_PLAN_MIN_CART_CENTS = 5000; // $50 CAD

/** Festival start date — all installments must complete before this */
export const FESTIVAL_START_DATE = '2027-08-12';

export const FASHION_PLAN_ID = 'fashion-2-pay-2027';
export const FASHION_TICKET_SLUGS = ['fashion-ga', 'fashion-vip', 'fashion-vip-plus'] as const;

/** Proposal only: deliberately excluded from defaults and seeding until approved. */
export const PROPOSED_FASHION_PLAN_CONFIG: PaymentPlanConfig = {
  id: FASHION_PLAN_ID,
  planName: 'Fashion - 2 Monthly Payments',
  numPayments: 2,
  minCartCents: 2900,
  availableUntil: '2027-01-26T00:00:00-07:00', // Inclusive January 25 cutoff (MST)
  isActive: false,
  festivalYear: 2027,
  eventDate: '2027-02-25',
  pricingMode: 'fixed',
  eligibleTicketSlugs: [...FASHION_TICKET_SLUGS],
};

export function isFashionEventTicket(slug: string): boolean {
  return (FASHION_TICKET_SLUGS as readonly string[]).includes(slug);
}

/**
 * Static plan definitions.
 *
 * In production these live in the `payment_plan_configs` Firestore collection
 * and are fetched at runtime. This constant provides the canonical defaults
 * for seeding and for client-side rendering when Firestore isn't reachable.
 */
export const DEFAULT_PLAN_CONFIGS: PaymentPlanConfig[] = [
  {
    id: '2-pay-2027',
    planName: '2 Monthly Payments',
    numPayments: 2,
    minCartCents: PAYMENT_PLAN_MIN_CART_CENTS,
    availableUntil: '2027-06-12T00:00:00-06:00', // 2 months before festival
    isActive: true,
    festivalYear: 2027,
  },
  {
    id: '3-pay-2027',
    planName: '3 Monthly Payments',
    numPayments: 3,
    minCartCents: PAYMENT_PLAN_MIN_CART_CENTS,
    availableUntil: '2027-05-12T00:00:00-06:00', // 3 months before festival
    isActive: true,
    festivalYear: 2027,
  },
  {
    id: '6-pay-2027',
    planName: '6 Monthly Payments',
    numPayments: 6,
    minCartCents: PAYMENT_PLAN_MIN_CART_CENTS,
    availableUntil: '2027-02-12T00:00:00-06:00', // 6 months before festival
    isActive: true,
    festivalYear: 2027,
  },
];

// ---------------------------------------------------------------------------
// Eligibility
// ---------------------------------------------------------------------------

/**
 * Check whether a cart total qualifies for payment plans.
 */
export function isPaymentPlanEligible(
  cartTotalCents: number,
  minCartCents: number = PAYMENT_PLAN_MIN_CART_CENTS,
): boolean {
  return Number.isSafeInteger(cartTotalCents) && cartTotalCents > 0 &&
    Number.isSafeInteger(minCartCents) && minCartCents >= 0 && cartTotalCents >= minCartCents;
}

function edmontonDate(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Edmonton', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);
}

/** Match monthly billing's original UTC anchor, clamping short months. */
export function getFinalInstallmentDate(purchaseDate: Date, numPayments: number): Date {
  const finalDate = new Date(purchaseDate);
  const day = finalDate.getUTCDate();
  finalDate.setUTCDate(1);
  finalDate.setUTCMonth(finalDate.getUTCMonth() + numPayments - 1);
  const monthEnd = new Date(Date.UTC(
    finalDate.getUTCFullYear(), finalDate.getUTCMonth() + 1, 0,
  )).getUTCDate();
  finalDate.setUTCDate(Math.min(day, monthEnd));
  return finalDate;
}

export function getPaymentPlanAvailabilityError(
  config: PaymentPlanConfig,
  purchaseDate: Date = new Date(),
): string | null {
  if (!config.isActive) return 'This payment plan is no longer available';
  const cutoff = new Date(config.availableUntil);
  const eventDate = config.eventDate ?? FESTIVAL_START_DATE;
  if (!Number.isInteger(config.numPayments) || config.numPayments < 2 ||
      config.numPayments > 12 || !Number.isSafeInteger(config.minCartCents) ||
      config.minCartCents < 0 || !Number.isFinite(cutoff.getTime()) ||
      !Number.isFinite(purchaseDate.getTime()) ||
      !/^\d{4}-\d{2}-\d{2}$/.test(eventDate) ||
      !Number.isFinite(Date.parse(eventDate)) ||
      new Date(eventDate).toISOString().slice(0, 10) !== eventDate) {
    return 'Invalid payment plan configuration';
  }
  if (purchaseDate >= cutoff) return 'This payment plan option has expired';
  if (edmontonDate(getFinalInstallmentDate(purchaseDate, config.numPayments)) > eventDate) {
    return 'All installments must be scheduled on or before the event date';
  }
  return null;
}

export function getPaymentPlanCartError(
  config: PaymentPlanConfig,
  tickets: TicketType[],
): string | null {
  if (!tickets.length) return 'Cart is empty';
  const fashionCount = tickets.filter((ticket) => isFashionEventTicket(ticket.slug)).length;
  if (fashionCount && fashionCount !== tickets.length) {
    return 'Fashion event tickets and festival passes require separate payment plan carts';
  }
  if (fashionCount || config.id === FASHION_PLAN_ID) {
    const exactSlugs = config.eligibleTicketSlugs;
    if (fashionCount !== tickets.length || config.id !== FASHION_PLAN_ID ||
        config.pricingMode !== 'fixed' || config.eventDate !== '2027-02-25' ||
        config.festivalYear !== 2027 || !exactSlugs || exactSlugs.length !== 3 ||
        !FASHION_TICKET_SLUGS.every((slug) => exactSlugs.includes(slug))) {
      return 'Fashion event tickets require their dedicated payment plan';
    }
  }
  for (const ticket of tickets) {
    if (!ticket.isActive || !Number.isSafeInteger(ticket.basePrice) || ticket.basePrice <= 0 ||
        ticket.currency?.toLowerCase() !== 'cad') return 'Invalid or unavailable ticket';
    if ((config.eligibleTicketSlugs && !config.eligibleTicketSlugs.includes(ticket.slug)) ||
        (ticket.paymentPlanIds && (!config.id || !ticket.paymentPlanIds.includes(config.id)))) {
      return 'This payment plan is not available for every ticket in the cart';
    }
    if ((ticket.pricingMode ?? 'tiered') !== (config.pricingMode ?? 'tiered') ||
        (ticket.festivalYear !== undefined && ticket.festivalYear !== config.festivalYear) ||
        (config.eventDate && ticket.eventDate !== config.eventDate)) {
      return 'Ticket and payment plan policies do not match';
    }
    if (fashionCount && (ticket.festivalYear !== 2027 ||
        !ticket.paymentPlanIds?.includes(FASHION_PLAN_ID))) {
      return 'Fashion ticket payment plan configuration is incomplete';
    }
  }
  return null;
}

/** Shared by checkout and payment-option UI; fixed tickets never enter discount stacking. */
export function calculatePaymentPlanCart(
  items: { ticketType: TicketType; quantity: number }[],
  tier: PricingTier,
  isMember: boolean = false,
) {
  let fixedCents = 0;
  let tieredCents = 0;
  let passCount = 0;
  for (const { ticketType, quantity } of items) {
    if (ticketType.pricingMode === 'fixed' || isFashionEventTicket(ticketType.slug)) {
      fixedCents += ticketType.basePrice * quantity;
    } else {
      tieredCents += calculateDiscountedPrice(ticketType.basePrice, tier) * quantity;
      passCount += quantity;
    }
  }
  const discounts = applyDiscountStack(tieredCents, { passCount, isMember });
  return {
    ...discounts,
    subtotalCents: fixedCents + tieredCents,
    finalCents: fixedCents + discounts.finalCents,
  };
}

/**
 * Filter plan configs to only those available for the given cart total
 * and purchase date.
 */
export function getAvailablePaymentPlans(
  configs: PaymentPlanConfig[],
  cartTotalCents: number,
  purchaseDate?: Date,
  tickets?: TicketType[],
): PaymentPlanConfig[] {
  const now = purchaseDate ?? new Date();

  return configs.filter((config) => {
    if (getPaymentPlanAvailabilityError(config, now)) return false;
    if (!isPaymentPlanEligible(cartTotalCents, config.minCartCents)) return false;
    if (tickets && getPaymentPlanCartError(config, tickets)) return false;
    // A scoped plan must never be shown without a resolved cart to validate.
    if (!tickets && (config.eligibleTicketSlugs || config.id === FASHION_PLAN_ID)) return false;
    return true;
  });
}

// ---------------------------------------------------------------------------
// Installment calculation
// ---------------------------------------------------------------------------

/**
 * Calculate the installment breakdown for a given total and plan config.
 *
 * The remainder (if total doesn't divide evenly) is added to the first
 * payment so the buyer pays slightly more upfront and less later.
 */
export function calculateInstallments(
  totalCents: number,
  config: PaymentPlanConfig,
): InstallmentBreakdown {
  const { numPayments } = config;
  if (!Number.isInteger(numPayments) || numPayments < 2 || numPayments > 12 ||
      !Number.isSafeInteger(totalCents) || totalCents < numPayments) {
    throw new Error('Invalid installment amount or payment count');
  }

  // Regular installment = floor(total / numPayments)
  const installmentCents = Math.floor(totalCents / numPayments);
  // First payment absorbs the remainder
  const remainder = totalCents - installmentCents * numPayments;
  const firstPaymentCents = installmentCents + remainder;

  return {
    planConfigId: config.id ?? '',
    planName: config.planName,
    totalCents,
    numPayments,
    installmentCents,
    firstPaymentCents,
    availableUntil: config.availableUntil,
  };
}

/**
 * Format an installment breakdown for display.
 * e.g. "$182.50 today, then 2 x $182.50/mo"
 */
export function formatInstallmentSummary(
  breakdown: InstallmentBreakdown,
  currency: string = 'CAD',
): string {
  const fmt = (cents: number) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(
      cents / 100,
    );

  const remainingPayments = breakdown.numPayments - 1;

  if (breakdown.firstPaymentCents === breakdown.installmentCents) {
    return `${breakdown.numPayments} x ${fmt(breakdown.installmentCents)}/mo`;
  }

  return `${fmt(breakdown.firstPaymentCents)} today, then ${remainingPayments} x ${fmt(breakdown.installmentCents)}/mo`;
}

// ---------------------------------------------------------------------------
// Discount stacking
// ---------------------------------------------------------------------------

/** Group discount: 25% off when purchasing 3+ passes */
export const GROUP_DISCOUNT_THRESHOLD = 3;
export const GROUP_DISCOUNT_PERCENT = 25;

/** Member discount: 50% off for C-Tribe Society members */
export const MEMBER_DISCOUNT_PERCENT = 50;

/**
 * Apply discount stack to a cart total.
 *
 * Order: presale (already reflected in Stripe prices) → group → member.
 * Presale is baked into the Stripe Price IDs, so this function only
 * handles group and member discounts on top of the tier price.
 */
export function applyDiscountStack(
  subtotalCents: number,
  opts: {
    passCount: number;
    isMember: boolean;
  },
): { finalCents: number; groupDiscountCents: number; memberDiscountCents: number } {
  let running = subtotalCents;
  let groupDiscountCents = 0;
  let memberDiscountCents = 0;

  // Group discount (3+ passes)
  if (opts.passCount >= GROUP_DISCOUNT_THRESHOLD) {
    groupDiscountCents = Math.round(running * (GROUP_DISCOUNT_PERCENT / 100));
    running -= groupDiscountCents;
  }

  // Member discount (50% for Society members)
  if (opts.isMember) {
    memberDiscountCents = Math.round(running * (MEMBER_DISCOUNT_PERCENT / 100));
    running -= memberDiscountCents;
  }

  return {
    finalCents: running,
    groupDiscountCents,
    memberDiscountCents,
  };
}
